import { SVGProps } from "react";

export type IconName =
  | "home"
  | "in"
  | "out"
  | "truck"
  | "chart"
  | "plus"
  | "minus"
  | "check"
  | "chev-r"
  | "chev-l"
  | "chev-d"
  | "chev-u"
  | "scan"
  | "search"
  | "clock"
  | "bolt"
  | "star"
  | "pin"
  | "box"
  | "sync"
  | "more"
  | "bell"
  | "edit"
  | "trash"
  | "filter"
  | "arrow-r"
  | "arrow-l"
  | "arrow-u"
  | "flag"
  | "note"
  | "tag"
  | "tiktok"
  | "shopify"
  | "user"
  | "lock"
  | "x"
  | "info";

type Props = { name: IconName; size?: number } & Omit<SVGProps<SVGSVGElement>, "name">;

const base = { fill: "none", strokeLinecap: "round", strokeLinejoin: "round" } as const;

export function Icon({ name, size = 18, ...rest }: Props) {
  const s = size;
  const sw = 1.6;
  switch (name) {
    case "home":
      return (
        <svg width={s} height={s} viewBox="0 0 18 18" {...base} {...rest}>
          <path d="M3 8L9 3l6 5v7a1 1 0 0 1-1 1h-3v-5H7v5H4a1 1 0 0 1-1-1V8z" stroke="currentColor" strokeWidth={sw} />
        </svg>
      );
    case "in":
      return (
        <svg width={s} height={s} viewBox="0 0 18 18" {...base} {...rest}>
          <path d="M9 2v9m0 0l-3.5-3.5M9 11l3.5-3.5M3 13v1.5A1.5 1.5 0 0 0 4.5 16h9a1.5 1.5 0 0 0 1.5-1.5V13" stroke="currentColor" strokeWidth={sw} />
        </svg>
      );
    case "out":
      return (
        <svg width={s} height={s} viewBox="0 0 18 18" {...base} {...rest}>
          <path d="M9 11V2m0 0L5.5 5.5M9 2l3.5 3.5M3 13v1.5A1.5 1.5 0 0 0 4.5 16h9a1.5 1.5 0 0 0 1.5-1.5V13" stroke="currentColor" strokeWidth={sw} />
        </svg>
      );
    case "truck":
      return (
        <svg width={s} height={s} viewBox="0 0 18 18" {...base} {...rest}>
          <path d="M1 4h9v8H1V4zm9 3h4l3 2.5V12h-7V7zM4.5 14a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3zm9 0a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z" stroke="currentColor" strokeWidth="1.4" />
        </svg>
      );
    case "chart":
      return (
        <svg width={s} height={s} viewBox="0 0 18 18" {...base} {...rest}>
          <path d="M2 15h14M4 12V8m4 4V5m4 7v-3m4 3V7" stroke="currentColor" strokeWidth={sw} />
        </svg>
      );
    case "plus":
      return (
        <svg width={s} height={s} viewBox="0 0 14 14" {...base} {...rest}>
          <path d="M7 2v10M2 7h10" stroke="currentColor" strokeWidth="2" />
        </svg>
      );
    case "minus":
      return (
        <svg width={s} height={s} viewBox="0 0 14 14" {...base} {...rest}>
          <path d="M2 7h10" stroke="currentColor" strokeWidth="2" />
        </svg>
      );
    case "check":
      return (
        <svg width={s} height={s} viewBox="0 0 12 12" {...base} {...rest}>
          <path d="M2.5 6L5 8.5 9.5 3.5" stroke="currentColor" strokeWidth="2" />
        </svg>
      );
    case "chev-r":
      return (
        <svg width={s} height={s} viewBox="0 0 14 14" {...base} {...rest}>
          <path d="M5 3l4 4-4 4" stroke="currentColor" strokeWidth={sw} />
        </svg>
      );
    case "chev-l":
      return (
        <svg width={s} height={s} viewBox="0 0 14 14" {...base} {...rest}>
          <path d="M9 3L5 7l4 4" stroke="currentColor" strokeWidth={sw} />
        </svg>
      );
    case "chev-d":
      return (
        <svg width={s} height={s} viewBox="0 0 14 14" {...base} {...rest}>
          <path d="M3 5l4 4 4-4" stroke="currentColor" strokeWidth={sw} />
        </svg>
      );
    case "chev-u":
      return (
        <svg width={s} height={s} viewBox="0 0 14 14" {...base} {...rest}>
          <path d="M3 9l4-4 4 4" stroke="currentColor" strokeWidth={sw} />
        </svg>
      );
    case "scan":
      return (
        <svg width={s} height={s} viewBox="0 0 18 18" {...base} {...rest}>
          <path d="M2 5V3h2M16 5V3h-2M2 13v2h2M16 13v2h-2" stroke="currentColor" strokeWidth={sw} />
          <path d="M2 9h14" stroke="currentColor" strokeWidth={sw} />
        </svg>
      );
    case "search":
      return (
        <svg width={s} height={s} viewBox="0 0 14 14" {...base} {...rest}>
          <circle cx="6" cy="6" r="4" stroke="currentColor" strokeWidth={sw} />
          <path d="M9 9l3 3" stroke="currentColor" strokeWidth={sw} />
        </svg>
      );
    case "clock":
      return (
        <svg width={s} height={s} viewBox="0 0 12 12" {...base} {...rest}>
          <circle cx="6" cy="6" r="4.5" stroke="currentColor" strokeWidth="1.3" />
          <path d="M6 4v2.2L7.5 7.5" stroke="currentColor" strokeWidth="1.3" />
        </svg>
      );
    case "bolt":
      return (
        <svg width={s} height={s} viewBox="0 0 12 12" {...base} {...rest}>
          <path d="M7 1L2 7h3l-1 4 5-6H6l1-4z" fill="currentColor" />
        </svg>
      );
    case "star":
      return (
        <svg width={s} height={s} viewBox="0 0 12 12" {...base} {...rest}>
          <path d="M6 1l1.5 3 3.5.5-2.5 2.5.6 3.5L6 8.8 2.9 10.5 3.5 7 1 4.5 4.5 4 6 1z" fill="currentColor" />
        </svg>
      );
    case "pin":
      return (
        <svg width={s} height={s} viewBox="0 0 12 12" {...base} {...rest}>
          <path d="M6 1c2 0 3.5 1.5 3.5 3.5C9.5 7 6 11 6 11S2.5 7 2.5 4.5C2.5 2.5 4 1 6 1z" stroke="currentColor" strokeWidth="1.3" fill="none" />
          <circle cx="6" cy="4.5" r="1.2" fill="currentColor" />
        </svg>
      );
    case "box":
      return (
        <svg width={s} height={s} viewBox="0 0 14 14" {...base} {...rest}>
          <path d="M7 1L1.5 3.5v7L7 13l5.5-2.5v-7L7 1zM1.5 3.5L7 6m0 0l5.5-2.5M7 6v7" stroke="currentColor" strokeWidth="1.3" />
        </svg>
      );
    case "sync":
      return (
        <svg width={s} height={s} viewBox="0 0 12 12" {...base} {...rest}>
          <path d="M2 6a4 4 0 0 1 7-2.6M10 6a4 4 0 0 1-7 2.6M9 1.5v2H7M3 10.5v-2h2" stroke="currentColor" strokeWidth="1.3" />
        </svg>
      );
    case "more":
      return (
        <svg width={s} height={s} viewBox="0 0 16 16" {...base} {...rest}>
          <circle cx="3" cy="8" r="1.3" fill="currentColor" />
          <circle cx="8" cy="8" r="1.3" fill="currentColor" />
          <circle cx="13" cy="8" r="1.3" fill="currentColor" />
        </svg>
      );
    case "bell":
      return (
        <svg width={s} height={s} viewBox="0 0 16 16" {...base} {...rest}>
          <path d="M8 2a4 4 0 0 0-4 4v3l-1 2h10l-1-2V6a4 4 0 0 0-4-4zM6.5 13a1.5 1.5 0 0 0 3 0" stroke="currentColor" strokeWidth="1.4" />
        </svg>
      );
    case "edit":
      return (
        <svg width={s} height={s} viewBox="0 0 12 12" {...base} {...rest}>
          <path d="M1.5 10.5L4 10l6-6-2-2-6 6-.5 2.5z" stroke="currentColor" strokeWidth="1.3" />
        </svg>
      );
    case "trash":
      return (
        <svg width={s} height={s} viewBox="0 0 12 12" {...base} {...rest}>
          <path d="M2.5 3h7m-6 0v6.5A1 1 0 0 0 4.5 10.5h3a1 1 0 0 0 1-1V3m-3.5 0V1.5h2V3" stroke="currentColor" strokeWidth="1.3" />
        </svg>
      );
    case "filter":
      return (
        <svg width={s} height={s} viewBox="0 0 14 14" {...base} {...rest}>
          <path d="M2 3h10l-3.5 5v4l-3-1.5V8L2 3z" stroke="currentColor" strokeWidth="1.4" />
        </svg>
      );
    case "arrow-r":
      return (
        <svg width={s} height={s} viewBox="0 0 14 14" {...base} {...rest}>
          <path d="M3 7h8m0 0L8 4m3 3L8 10" stroke="currentColor" strokeWidth={sw} />
        </svg>
      );
    case "arrow-l":
      return (
        <svg width={s} height={s} viewBox="0 0 14 14" {...base} {...rest}>
          <path d="M11 7H3m0 0l3 3M3 7l3-3" stroke="currentColor" strokeWidth={sw} />
        </svg>
      );
    case "arrow-u":
      return (
        <svg width={s} height={s} viewBox="0 0 14 14" {...base} {...rest}>
          <path d="M7 11V3m0 0L4 6m3-3l3 3" stroke="currentColor" strokeWidth={sw} />
        </svg>
      );
    case "flag":
      return (
        <svg width={s} height={s} viewBox="0 0 12 12" {...base} {...rest}>
          <path d="M2.5 11V2h6l-1.5 2 1.5 2H2.5" stroke="currentColor" strokeWidth="1.4" />
        </svg>
      );
    case "note":
      return (
        <svg width={s} height={s} viewBox="0 0 12 12" {...base} {...rest}>
          <path d="M2 2h6l2 2v6H2V2zm6 0v2h2M4 6h4M4 8h3" stroke="currentColor" strokeWidth="1.3" />
        </svg>
      );
    case "tag":
      return (
        <svg width={s} height={s} viewBox="0 0 12 12" {...base} {...rest}>
          <path d="M1.5 1.5h4L11 7l-4 4-5.5-5.5v-4z" stroke="currentColor" strokeWidth="1.3" />
          <circle cx="3.5" cy="3.5" r="0.8" fill="currentColor" />
        </svg>
      );
    case "tiktok":
      return (
        <svg width={s} height={s} viewBox="0 0 14 14" {...base} {...rest}>
          <path d="M9 1v7.5a2.5 2.5 0 1 1-2.5-2.5h.5v2A.5.5 0 1 0 7.5 8V1H9zm0 0c0 1.5 1 2.5 2.5 2.5" stroke="currentColor" strokeWidth="1.3" />
        </svg>
      );
    case "shopify":
      return (
        <svg width={s} height={s} viewBox="0 0 14 14" {...base} {...rest}>
          <path d="M10 3.5c0-.8-.5-1.5-1.3-1.5-.6 0-1.1.4-1.4 1-.4-.1-.7-.1-1.1 0L3.8 4.2 3 12l6.4 1L11 4l-1-.5z" stroke="currentColor" strokeWidth="1.3" />
        </svg>
      );
    case "user":
      return (
        <svg width={s} height={s} viewBox="0 0 14 14" {...base} {...rest}>
          <circle cx="7" cy="4.5" r="2.2" stroke="currentColor" strokeWidth="1.3" />
          <path d="M2.5 12c0-2.4 2-4 4.5-4s4.5 1.6 4.5 4" stroke="currentColor" strokeWidth="1.3" />
        </svg>
      );
    case "lock":
      return (
        <svg width={s} height={s} viewBox="0 0 14 14" {...base} {...rest}>
          <rect x="2.5" y="6.5" width="9" height="6" rx="1.2" stroke="currentColor" strokeWidth="1.3" />
          <path d="M4.5 6.5V4.5a2.5 2.5 0 1 1 5 0v2" stroke="currentColor" strokeWidth="1.3" />
        </svg>
      );
    case "x":
      return (
        <svg width={s} height={s} viewBox="0 0 14 14" {...base} {...rest}>
          <path d="M3 3l8 8M11 3l-8 8" stroke="currentColor" strokeWidth={sw} />
        </svg>
      );
    case "info":
      return (
        <svg width={s} height={s} viewBox="0 0 14 14" {...base} {...rest}>
          <circle cx="7" cy="7" r="5.5" stroke="currentColor" strokeWidth="1.3" />
          <path d="M7 6.5v3.5M7 4.5v.01" stroke="currentColor" strokeWidth="1.4" />
        </svg>
      );
  }
}
